import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { RowShares, UUID } from "@/lib/types";

// Read + manage the people/departments a ticket (row) is shared with. Sharing
// changes who can reach the row, so it invalidates the shares query, the "Mit dir
// geteilt" feed, and the bell (a share fires a notification).
export function useRowShares(rowId: UUID | undefined, enabled = true) {
  const qc = useQueryClient();
  const key = rowId ? queryKeys.rowShares(rowId) : ["rows", "void", "shares"];

  const list = useQuery({
    enabled: Boolean(rowId) && enabled,
    queryKey: key,
    queryFn: () => api.listRowShares(rowId as UUID),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: queryKeys.rowShares(rowId as UUID) });
    void qc.invalidateQueries({ queryKey: queryKeys.sharedTickets });
    void qc.invalidateQueries({ queryKey: queryKeys.notifications });
    // A share writes a row.shared activity event → refresh the open Verlauf.
    void qc.invalidateQueries({ queryKey: queryKeys.rowActivity(rowId as UUID) });
  }

  const addPeople = useMutation<RowShares, Error, UUID[]>({
    mutationFn: (userIds) => api.shareRow(rowId as UUID, { user_ids: userIds }),
    onSuccess: invalidate,
  });
  const addDepartments = useMutation<RowShares, Error, UUID[]>({
    mutationFn: (workspaceIds) => api.shareRow(rowId as UUID, { workspace_ids: workspaceIds }),
    onSuccess: invalidate,
  });
  const removePerson = useMutation<void, Error, UUID>({
    mutationFn: (userId) => api.unshareRow(rowId as UUID, { user_id: userId }),
    onSuccess: invalidate,
  });
  const removeDepartment = useMutation<void, Error, UUID>({
    mutationFn: (workspaceId) => api.unshareRow(rowId as UUID, { workspace_id: workspaceId }),
    onSuccess: invalidate,
  });

  const shares: RowShares = list.data ?? { users: [], departments: [] };
  return {
    shares,
    isLoading: list.isLoading,
    addPeople,
    addDepartments,
    removePerson,
    removeDepartment,
  };
}
